import { z } from "zod";

import { CurrencySchema, ProfessionSchema } from "api/schemas/shared";

// Active patients
const GetActivePatientsArgsSchema = z.object({
  health_care_organization_id: z.number(),
  params: z.object({
    hcp_id: z.number().optional(),
    start_time: z.string(),
    therapist_id: z.number().optional(),
  }),
});
export type GetActivePatientsArgs = z.infer<typeof GetActivePatientsArgsSchema>;

const ActivePatientsSchema = z.object({
  month: z.string(),
  total_hep: z.number(),
  total_rtm: z.number(),
});
export type ActivePatients = z.infer<typeof ActivePatientsSchema>;

export const GetActivePatientsResponseSchema = z.object({
  items: z.array(ActivePatientsSchema),
});
export type GetActivePatientsResponse = z.infer<typeof GetActivePatientsResponseSchema>;

// Earnings
const EarningsViewSchema = z.enum(["daily", "weekly", "monthly"]);
export type EarningsView = z.infer<typeof EarningsViewSchema>;

const GetEarningsArgsSchema = z.object({
  health_care_organization_id: z.number(),
  params: z.object({
    hcp_id: z.number().optional(),
    end_time: z.string(),
    start_time: z.string(),
    view: EarningsViewSchema,
    therapist_id: z.number().optional(),
  }),
});
export type GetEarningsArgs = z.infer<typeof GetEarningsArgsSchema>;

const EarningItemSchema = z.object({
  completed: z.number(),
  currency: CurrencySchema,
  discarded: z.number(),
  pending: z.number(),
  total: z.number(),
});

const EarningsSchema = z.object({
  earnings: z.record(z.string(), z.record(z.string(), EarningItemSchema)),
  total: EarningItemSchema,
});

export type Earnings = z.infer<typeof EarningsSchema>;

export const GetEarningsResponseSchema = EarningsSchema;

// Enrolled patients
const GetEnrolledPatientsArgsSchema = z.object({
  health_care_organization_id: z.number(),
  params: z.object({
    hcp_id: z.number().optional(),
    start_time: z.string(),
    therapist_id: z.number().optional(),
  }),
});
export type GetEnrolledPatientsArgs = z.infer<typeof GetEnrolledPatientsArgsSchema>;

const EnrolledPatientsSchema = z.object({
  month: z.string(),
  total_hep: z.number(),
  total_rtm: z.number(),
});
export type EnrolledPatients = z.infer<typeof EnrolledPatientsSchema>;

export const GetEnrolledPatientsResponseSchema = z.object({
  items: z.array(EnrolledPatientsSchema),
});
export type GetEnrolledPatientsResponse = z.infer<typeof GetEnrolledPatientsResponseSchema>;

// Enrollment goals

const GetEnrollmentGoalsArgsSchema = z.object({
  health_care_organization_id: z.number(),
});
export type GetEnrollmentGoalsArgs = z.infer<typeof GetEnrollmentGoalsArgsSchema>;

export const BaseEnrollmentGoalSchema = z.object({
  bonus_cents: z.number(),
  bonus_currency: CurrencySchema,
  effective_from: z.string(),
  enrollment_goal: z.number(),
  id: z.number(),
});
export type BaseEnrollmentGoal = z.infer<typeof BaseEnrollmentGoalSchema>;

const EnrollmentGoalSchema = z.object({
  ...BaseEnrollmentGoalSchema.shape,
  is_current: z.boolean(),
});
export type EnrollmentGoal = z.infer<typeof EnrollmentGoalSchema>;

export const GetEnrollmentGoalsResponseSchema = z.object({
  items: z.array(EnrollmentGoalSchema),
});
export type GetEnrollmentGoalsResponse = z.infer<typeof GetEnrollmentGoalsResponseSchema>;

export const EnrollmentGoalPayloadSchema = z.object({
  bonus_cents: z.number(),
  bonus_currency: CurrencySchema,
  enrollment_goal: z.number(),
});
export type EnrollmentGoalPayload = z.infer<typeof EnrollmentGoalPayloadSchema>;

const PutEnrollmentGoalArgsSchema = z.object({
  health_care_organization_id: z.number(),
  payload: EnrollmentGoalPayloadSchema,
});
export type PutEnrollmentGoalArgs = z.infer<typeof PutEnrollmentGoalArgsSchema>;

// Commission rates
const GetCommissionRatesArgsSchema = z.object({
  health_care_organization_id: z.number(),
});
export type GetCommissionRatesArgs = z.infer<typeof GetCommissionRatesArgsSchema>;

const CommissionRateSchema = z.object({
  commission_bps: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const CommissionRatesResponseSchema = z.object({
  current: CommissionRateSchema.nullable(),
  previous: z.array(CommissionRateSchema),
});
export type GetCommissionRatesResponse = z.infer<typeof CommissionRatesResponseSchema>;

const CommissionRatePayloadSchema = z.object({
  commission_bps: z.number(),
});
export type CommissionRatePayload = z.infer<typeof CommissionRatePayloadSchema>;

const PostCommissionRatesArgsSchema = z.object({
  health_care_organization_id: z.number(),
  payload: CommissionRatePayloadSchema,
});
export type PostCommissionRatesArgs = z.infer<typeof PostCommissionRatesArgsSchema>;

// Health care providers
const GetHealthCareProvidersArgsSchema = z.object({
  health_care_organization_id: z.number(),
});
export type GetHealthCareProvidersArgs = z.infer<typeof GetHealthCareProvidersArgsSchema>;

const HealthCareProviderSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type HealthCareProvider = z.infer<typeof HealthCareProviderSchema>;

export const GetHealthCareProvidersResponseSchema = z.object({
  health_care_providers: z.array(HealthCareProviderSchema),
});
export type GetHealthCareProvidersResponse = z.infer<typeof GetHealthCareProvidersResponseSchema>;

// Performance summary

const GetPerformanceSummaryArgsSchema = z.object({
  health_care_organization_id: z.number(),
  params: z.object({
    month: z.string(),
  }),
});
export type GetPerformanceSummaryArgs = z.infer<typeof GetPerformanceSummaryArgsSchema>;

export const GetPerformanceSummaryResponseSchema = z.object({
  currency: CurrencySchema,
  enrollment_goal: z.number(),
  total_bonus_cents: z.number(),
  total_commission_cents: z.number(),
  total_earnings_cents: z.number(),
  total_enrolled: z.number(),
  total_goals: z.number(),
  total_goals_reached: z.number(),
});
export type GetPerformanceSummaryResponse = z.infer<typeof GetPerformanceSummaryResponseSchema>;

// Professionals
const GetProfessionalsArgsSchema = z.object({
  health_care_organization_id: z.number(),
  params: z
    .object({
      hcp_id: z.number().optional(),
      month: z.string().optional(),
    })
    .optional(),
});
export type GetProfessionalsArgs = z.infer<typeof GetProfessionalsArgsSchema>;

const ProfessionalStatusSchema = z.enum(["active", "deleted", "in_onboarding"]);
export type ProfessionalStatus = z.infer<typeof ProfessionalStatusSchema>;

const ProfessionalSchema = z.object({
  avatars: z.object({
    large: z.string().url().nullable(),
    medium: z.string().url().nullable(),
    small: z.string().url().nullable(),
  }),
  bonus_commission_cents: z.number(),
  care_event_commission_cents: z.number(),
  currency: CurrencySchema,
  earnings_cents: z.number(),
  email: z.string().email(),
  enrolled_patients: z.number(),
  enrollment_goal: z.number(),
  id: z.number(),
  name: z.string(),
  profession: ProfessionSchema,
  status: ProfessionalStatusSchema,
});
export type Professional = z.infer<typeof ProfessionalSchema>;

export const GetProfessionalsResponseSchema = z.object({
  professionals: z.array(ProfessionalSchema),
});
export type GetProfessionalsResponse = z.infer<typeof GetProfessionalsResponseSchema>;

// Billing portal URL
export const GetBillingPortalUrlArgsSchema = z.object({
  health_care_organization_id: z.number(),
});
export type GetBillingPortalUrlArgs = z.infer<typeof GetBillingPortalUrlArgsSchema>;

export const GetBillingPortalUrlResponseSchema = z.object({
  billing_portal_url: z.string(),
});
export type GetBillingPortalUrlResponse = z.infer<typeof GetBillingPortalUrlResponseSchema>;

// Invoices
export const GetInvoicesArgsSchema = z.object({
  health_care_organization_id: z.number(),
  params: z
    .object({
      status: z.string().optional(),
      month: z.string().optional(),
    })
    .optional(),
});
export type GetInvoicesArgs = z.infer<typeof GetInvoicesArgsSchema>;

const InvoiceSchema = z.object({
  amount_due: z.number(),
  billing_period: z.string().nullable(),
  created_at: z.string(),
  currency: CurrencySchema,
  description: z.string(),
  due_date: z.string().nullable(),
  hosted_invoice_url: z.string().nullable(),
  invoice_id: z.string(),
  status: z.string(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const GetInvoicesResponseSchema = z.object({
  invoices: z.array(InvoiceSchema),
});
export type GetInvoicesResponse = z.infer<typeof GetInvoicesResponseSchema>;

// Invoice Specification
export const GetInvoiceSpecificationArgsSchema = z.object({
  health_care_organization_id: z.number(),
  invoice_id: z.string(),
});
export type GetInvoiceSpecificationArgs = z.infer<typeof GetInvoiceSpecificationArgsSchema>;

const InvoiceSpecificationRowSchema = z.object({
  patient_name: z.string(),
  cpt_code: z.string(),
  date_of_service: z.string(),
  claim_value_cents: z.number(),
  fee_cents: z.number(),
  currency: CurrencySchema,
});
export type InvoiceSpecificationRow = z.infer<typeof InvoiceSpecificationRowSchema>;

export const GetInvoiceSpecificationResponseSchema = z.object({
  rows: z.array(InvoiceSpecificationRowSchema),
});
export type GetInvoiceSpecificationResponse = z.infer<typeof GetInvoiceSpecificationResponseSchema>;
